/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MessageSquareText, Info } from 'lucide-react';
import { listMySecondOpinions, getSecondOpinion } from '@/lib/marketplace';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { RequestForm } from '@/components/secondOpinion/RequestForm';
import { DetailPanel } from '@/components/secondOpinion/DetailPanel';
import { StatusBadge, urgencyMeta } from '@/components/secondOpinion/meta';

const SecondOpinionPage = () => {
  const { user } = useAuth();
  const isDoctor = (user?.role === 'professional') || (user?.category && user.category !== 'paziente');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { second_opinions } = await listMySecondOpinions();
      setItems(second_opinions || []);
    } catch (e) {
      console.error('SecondOpinion: load list failed', e);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    try {
      const { second_opinion } = await getSecondOpinion(id);
      setSelected(second_opinion);
      setSelectedId(id);
    } catch (e) {
      toast.error('Impossibile caricare i dettagli');
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 lg:py-10" data-testid="second-opinion-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-primary"><MessageSquareText className="h-5 w-5" /><span className="text-xs uppercase tracking-wider font-bold">Sprint C · Marketplace</span></div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Secondo Parere</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            {isDoctor
              ? 'Rispondi alle richieste di secondo parere ricevute dai pazienti.'
              : 'Chiedi un secondo parere clinico a un altro medico specializzato.'}
          </p>
        </div>
        {!isDoctor && (
          <Button onClick={() => { setShowForm(true); setSelectedId(null); setSelected(null); }} data-testid="so-new-btn">
            + Nuova richiesta
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        <div className="space-y-2">
          {loading ? (
            <><Skeleton className="h-20" /><Skeleton className="h-20" /></>
          ) : items.length === 0 ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {isDoctor ? 'Nessuna richiesta ricevuta.' : 'Nessuna richiesta inviata.'}
              </AlertDescription>
            </Alert>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                onClick={() => { setShowForm(false); loadDetail(it.id); }}
                className={cn(
                  'w-full text-left rounded-xl border p-3 transition-colors',
                  selectedId === it.id ? 'border-primary bg-primary/5' : 'border-border/60 hover:bg-accent/40'
                )}
                data-testid={`so-item-${it.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <StatusBadge status={it.status} />
                  <Badge variant="outline" className={cn('text-[10px]', urgencyMeta[it.urgency]?.cls)}>{urgencyMeta[it.urgency]?.label || it.urgency}</Badge>
                </div>
                <div className="text-sm font-medium line-clamp-2">{it.clinical_question}</div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {isDoctor ? (it.patient_name || 'Paziente') : (it.doctor_name || '—')} · {new Date(it.created_at).toLocaleDateString('it')}
                </div>
              </button>
            ))
          )}
        </div>

        <div>
          {showForm && !isDoctor ? (
            <RequestForm onDone={(created) => { setShowForm(false); load(); loadDetail(created.id); }} />
          ) : selected ? (
            <DetailPanel item={selected} isDoctor={isDoctor} onBack={() => { setSelected(null); setSelectedId(null); }} onRefresh={() => { load(); loadDetail(selected.id); }} />
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <MessageSquareText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">{isDoctor ? 'Seleziona una richiesta per rispondere.' : 'Seleziona una richiesta oppure creane una nuova.'}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecondOpinionPage;
